import React, { useState } from 'react';
import { groupSessionService, attendanceService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import AttendanceModal from '../components/admin/AttendanceModal';
import {
  Users, RefreshCw, XCircle, Loader2, Calendar, ClipboardList,
} from 'lucide-react';

const STATUS_STYLES = {
  SCHEDULED: { bg: 'bg-blue-50',  text: 'text-blue-700',  border: 'border-blue-200',  label: 'Scheduled' },
  LIVE:      { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Live'      },
  COMPLETED: { bg: 'bg-forest-50',text: 'text-forest-700',border: 'border-forest-200',label: 'Completed' },
  CANCELLED: { bg: 'bg-rust-50',  text: 'text-rust-500',  border: 'border-rust-200',  label: 'Cancelled' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.SCHEDULED;
  return (
    <span className={`text-[10px] font-semibold border px-2.5 py-0.5 rounded-full ${s.bg} ${s.text} ${s.border}`}>
      {s.label}
    </span>
  );
};

const AdminGroupSessionsPage = () => {
  const [statusFilter, setStatusFilter]       = useState('');
  const [page,         setPage]               = useState(1);
  const [attendanceTarget, setAttendanceTarget] = useState(null);

  const { data, loading, error, refetch } = useApi(
    () => groupSessionService.adminGetAll({ page, limit: 25, status: statusFilter || undefined }),
    [page, statusFilter]
  );

  const sessions = Array.isArray(data) ? data : (data?.sessions ?? []);
  const LIMIT    = 25;
  const hasNextPage = sessions.length === LIMIT;

  return (
    <DashboardLayout title="Group Sessions">
      <div className="space-y-5 max-w-6xl">

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white text-forest-700">
            <option value="">All Statuses</option>
            {Object.entries(STATUS_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={refetch}
            className="p-2.5 rounded-xl border border-cream-200 text-forest-500 hover:bg-cream-50 transition-colors">
            <RefreshCw size={15} />
          </button>
        </div>

        <p className="text-sm text-forest-500">{loading ? 'Loading...' : `${sessions.length} group session${sessions.length !== 1 ? 's' : ''} on this page`}</p>

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <XCircle size={15} /> Could not load group sessions.
          </div>
        )}

        <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-cream-50 border-b border-cream-100 text-[10px] font-semibold text-forest-500 uppercase tracking-widest">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Host</div>
            <div className="col-span-2">Scheduled</div>
            <div className="col-span-1">Participants</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2" />
          </div>

          {loading ? (
            <div className="divide-y divide-cream-50">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-14 px-5 flex items-center gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-cream-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-cream-200 rounded w-1/3" />
                    <div className="h-2.5 bg-cream-100 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-16">
              <Users size={32} className="text-forest-200 mx-auto mb-3" />
              <p className="font-serif text-xl text-forest-600">No group sessions found</p>
              <p className="text-sm text-forest-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div>
              {sessions.map(s => {
                const hostName = s.expertProfile?.user
                  ? `${s.expertProfile.user.firstName} ${s.expertProfile.user.lastName}`
                  : (s.hostUser ? `${s.hostUser.firstName} ${s.hostUser.lastName}` : '—');
                return (
                  <div key={s.id} className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center border-b border-cream-50 last:border-0 hover:bg-cream-50/50 transition-colors">
                    <div className="col-span-4 min-w-0">
                      <p className="text-sm text-forest-800 truncate">{s.title}</p>
                      <p className="text-[10px] text-forest-400">{s.sessionType} · {s.duration} min</p>
                    </div>
                    <div className="col-span-2 text-sm text-forest-700 truncate">{hostName}</div>
                    <div className="col-span-2">
                      <p className="text-xs text-forest-700">{new Date(s.scheduledAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      <p className="text-[10px] text-forest-400">{new Date(s.scheduledAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="col-span-1 flex items-center gap-1 text-sm text-forest-700">
                      <Users size={12} className="text-forest-400" /> {s._count?.participants ?? 0}/{s.maxParticipants}
                    </div>
                    <div className="col-span-1"><StatusBadge status={s.status} /></div>
                    <div className="col-span-2 flex justify-end">
                      <button onClick={() => setAttendanceTarget(s)}
                        className="flex items-center gap-1.5 text-xs font-medium border border-cream-200 text-forest-600 hover:border-forest-400 px-3 py-1.5 rounded-full transition-colors">
                        <ClipboardList size={12} /> Attendance
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {(page > 1 || hasNextPage) && (
          <div className="flex items-center justify-center gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 transition-colors">
              ← Prev
            </button>
            <span className="text-sm text-forest-500">Page {page}</span>
            <button disabled={!hasNextPage} onClick={() => setPage(p => p + 1)}
              className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 transition-colors">
              Next →
            </button>
          </div>
        )}
      </div>

      {attendanceTarget && (
        <AttendanceModal
          title={attendanceTarget.title}
          roomLabel={new Date(attendanceTarget.scheduledAt).toLocaleDateString('en-GH', { dateStyle: 'long' })}
          fetchFn={() => attendanceService.getForGroupSession(attendanceTarget.id)}
          syncFn={() => attendanceService.syncGroupSession(attendanceTarget.id)}
          onClose={() => setAttendanceTarget(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default AdminGroupSessionsPage;